// app/javascript/landing/sections/Technologies.tsx
import React, { useState } from "react";
import {
  motion,
  AnimatePresence,
  type Transition,
  type Variants,
} from "framer-motion";
import clsx from "clsx";

import { FullscreenVideoModal } from "../components/FullscreenVideoModal";

const transition: Transition = { duration: 1, ease: [0.25, 0.1, 0.25, 1] };
const variants: Variants = {
  hidden: { filter: "blur(10px)", transform: "translateY(20%)", opacity: 0 },
  visible: { filter: "blur(0)", transform: "translateY(0)", opacity: 1 },
};

type TechKey = "vr" | "ar" | "xr" | "web";

type Device = {
  id: string;
  label: string;
  imageSrc: string;
  alt?: string;
};

type Technology = {
  key: TechKey;
  shortLabel: string;
  title: string;
  paragraphs: string[];
  devices: Device[];
  videoUrl?: string;
};

const TECHNOLOGIES: Technology[] = [
  {
    key: "vr",
    shortLabel: "VR",
    title: "Realidad Virtual",
    paragraphs: [
      "Crea un entorno digital completo donde la persona, usando lentes especiales, puede “entrar” a un mundo 100% digital y manipular objetos.",
      "Ofrece una experiencia mucho más inmersiva y atractiva para la visualización de proyectos.",
    ],
    devices: [
      {
        id: "vr-headset",
        label: "Lentes VR",
        imageSrc: "/landing/tech/devices/vr-headset.png",
        alt: "Lentes VR",
      },
    ],
    videoUrl: "/landing/home/tech/videos/video-vr.mp4",
  },
  {
    key: "ar",
    shortLabel: "AR",
    title: "Realidad Aumentada",
    paragraphs: [
      "Permite ver elementos digitales superpuestos sobre el entorno real a través de la cámara de un celular o tablet.",
    ],
    devices: [
      {
        id: "vr-headset",
        label: "Lentes VR",
        imageSrc: "/landing/tech/devices/vr-headset.png",
        alt: "Lentes VR",
      },
      {
        id: "phone",
        label: "Celulares",
        imageSrc: "/landing/tech/devices/phone.png",
        alt: "Celulares",
      },
    ],
    videoUrl: "/landing/home/tech/videos/video-ar.mp4",
  },
  {
    key: "xr",
    shortLabel: "XR",
    title: "Realidad Extendida",
    paragraphs: [
      "Termino que contempla la implementación AR y VR en un mismo proyecto. En otras palabras, diferentes formas de tecnologías de inmersión en un mismo lugar.",
    ],
    devices: [
      {
        id: "vr-headset",
        label: "Lentes VR",
        imageSrc: "/landing/tech/devices/vr-headset.png",
        alt: "Lentes VR",
      },
      {
        id: "phone",
        label: "Celulares",
        imageSrc: "/landing/tech/devices/phone.png",
        alt: "Celulares",
      },
    ],
    videoUrl: "/landing/home/tech/videos/video-xr.mp4",
  },
  {
    key: "web",
    shortLabel: "WEB",
    title: "Web Inmersiva",
    paragraphs: [
      "Permite explorar proyectos desde cualquier navegador, sin necesidad de lentes ni aplicaciones.",
      "Puede incluir imagenes 360°, recorridos virtuales o maquetas digitales con las que el usuario puede interactuar de forma remota.",
    ],
    devices: [
      {
        id: "desktop",
        label: "Computadores",
        imageSrc: "/landing/tech/devices/desktop.png",
        alt: "Computadores",
      },
      {
        id: "phone",
        label: "Celulares",
        imageSrc: "/landing/tech/devices/phone.png",
        alt: "Celulares",
      },
    ],
  },
];

const techByKey: Record<TechKey, Technology> = TECHNOLOGIES.reduce(
  (acc, tech) => {
    acc[tech.key] = tech;
    return acc;
  },
  {} as Record<TechKey, Technology>
);

export function Technologies() {
  const [activeKey, setActiveKey] = useState<TechKey>("vr");
  const [videoTechKey, setVideoTechKey] = useState<TechKey | null>(null);

  const active = techByKey[activeKey];
  const openTech = videoTechKey ? techByKey[videoTechKey] : null;
  const isVideoOpen = !!openTech?.videoUrl;

  return (
    <section className="x-bg-red-400">
      {/* Modal de video a nivel de sección */}
      <FullscreenVideoModal
        open={isVideoOpen}
        src={openTech?.videoUrl ?? ""}
        title={openTech?.title}
        onClose={() => setVideoTechKey(null)}
      />

      <motion.div
        className="flex flex-col justify-center space-y-18"
        initial="hidden"
        whileInView="visible"
        transition={{ staggerChildren: 0.1 }}
      >
        <div
          className="py-24"
          style={{
            background: "url(/landing/bg/technologies-header.png)",
            backgroundSize: "cover",
            backgroundPosition: "center left",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="max-w-4xl mx-auto px-4 text-center space-y-1">
            <motion.p
              className="text-xl md:text-2xl text-white"
              transition={transition}
              variants={variants}
            >
              Conoce nuestras
            </motion.p>

            <motion.h2
              className="text-4xl md:text-5xl font-bold text-white uppercase"
              transition={transition}
              variants={variants}
            >
              Tecnologías
            </motion.h2>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 space-y-18 pb-18">
          {/* Contenido: tabs + detalle */}
          <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-stretch">
            {/* Tabs */}
            <div className="mb-4 md:mb-0 flex justify-center md:justify-start">
              <div className="relative">
                {/* Línea vertical solo desktop */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-1/2 top-5 bottom-5 w-1 -translate-x-1/2 bg-purple-3 hidden md:block"
                />

                <div className="flex flex-row md:flex-col gap-4 md:gap-5">
                  {TECHNOLOGIES.map((tech) => {
                    const isActive = tech.key === activeKey;

                    return (
                      <button
                        key={tech.key}
                        type="button"
                        onClick={() => setActiveKey(tech.key)}
                        className={clsx(
                          "relative z-[1] flex items-center justify-center rounded-full border w-16 h-16 md:w-18 md:h-18 text-sm md:text-base font-semibold transition-all duration-200",
                          isActive
                            ? "bg-purple-3 border-purple-3 text-white x-shadow-lg x-shadow-[#2B2A56]/30"
                            : "bg-white border-2 border-purple-3 text-purple-3 x-hover:bg-[#2B2A56]/5"
                        )}
                      >
                        {tech.shortLabel}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Detalle tecnología */}
            <div className="space-y-8 max-w-lg md:flex-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="space-y-6 h-full flex flex-col justify-between"
                >
                  <div>
                    <h3 className="text-2xl md:text-3xl font-semibold text-purple-3 mb-6">
                      {active.title}
                    </h3>
                    <div className="space-y-3 text-sm md:text-lg leading-6 text-purple-3/80">
                      {active.paragraphs.map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>
                  </div>

                  {/* Dispositivos + botón video */}
                  <div className="relative flex flex-wrap items-center gap-6">
                    {active.devices.map((device) => (
                      <div
                        key={device.id}
                        className="inline-flex flex-col items-center gap-1 text-purple-3"
                      >
                        <img
                          src={device.imageSrc}
                          alt={device.alt ?? device.label}
                          className="size-16 object-contain"
                        />
                        <span className="text-sm font-medium">
                          {device.label}
                        </span>
                      </div>
                    ))}

                    {active.videoUrl && (
                      <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
                        <button
                          type="button"
                          onClick={() => setVideoTechKey(active.key)}
                          className="btn-landing"
                        >
                          Ver video
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
